import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import {
  INPUT_MAX_VISUAL_WIDTH,
  INPUT_MIN_USABLE_WIDTH,
  SCREENREADER_ONLY_STYLES,
} from '../../../util/commonStyles'
import { CssVars, NeutralColorToken, SemanticColorToken } from '../../themes'
import { Type } from '../Type'
import { ErrorMessageManager } from './ErrorMessageManager'
import { DEFAULT_SIZES, type InputState } from './InternalInput'

const LABEL_COLOR_FOR_STATE = {
  default: undefined,
  disabled: NeutralColorToken.Fg2,
  error: SemanticColorToken.Error,
}

const DEFAULT_MIN_WIDTH =
  INPUT_MIN_USABLE_WIDTH +
  2 * DEFAULT_SIZES.border +
  DEFAULT_SIZES.paddingLeft +
  DEFAULT_SIZES.paddingRight

type Props = {
  /**
   * Children render prop that returns the actual input element to use. You almost certainly
   * want to be rendering an `InternalInput` in this.
   */
  children: (errorMessageId: string) => React.ReactNode
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Optional prop to visually hide the label (still readable by the screenreader)
   * defaults to false
   */
  hideLabel?: boolean
  /**
   * A label to show above the input, use `hideLabel` if you need the label to be visually hidden
   *
   * your must provide an external label if you need special layouts
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"` and
   * this component also has a `label` specified. If another component is rendering the `label`
   * for this input, that component should be rendering the error message as well.
   */
  errorMessage?: string
  /**
   * The min-width to use for the outermost element. This is needed to ensure that any label
   * text has the same min-width as the children rendered inside of this. Accepts any valid
   * CSS min-width value. Defaults to a value corresponding to the values of DEFAULT_SIZES
   * in InternalInput.
   */
  minWidth?: number | string
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do
   * this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

/**
 * A wrapper for all `*Input` components in Bento Web, which handles rendering any
 * label and error message when appropriate.
 */
export function InternalInputWrapper({
  children,
  errorMessage,
  hideLabel = false,
  label,
  minWidth = DEFAULT_MIN_WIDTH,
  overrideRootStyles,
  state = 'default',
}: Props) {
  const rootStyles = [
    {
      maxWidth: INPUT_MAX_VISUAL_WIDTH,
      minWidth,
      width: '100%',
    },
    overrideRootStyles,
  ]

  return (
    <ErrorMessageManager
      errorMessage={state === 'error' ? errorMessage : undefined}
    >
      {(errorMessageId, errorMessageElem) => {
        const input = children(errorMessageId)

        if (!label) {
          return (
            <div css={rootStyles}>
              {input}
              {errorMessageElem}
            </div>
          )
        }

        return (
          <div css={rootStyles}>
            <label css={{ width: '100%' }}>
              <div
                css={
                  hideLabel
                    ? SCREENREADER_ONLY_STYLES
                    : { marginBottom: CssVars.size.gutterWidthSm }
                }
              >
                <Type.Text color={LABEL_COLOR_FOR_STATE[state]} size="S">
                  {label}
                </Type.Text>
              </div>
              {input}
            </label>
            {errorMessageElem}
          </div>
        )
      }}
    </ErrorMessageManager>
  )
}
